import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { useFrame } from '@react-three/fiber';

interface SimpleLineProps {
    points: [number, number, number][];
    color?: THREE.Color | string | number;
    lineWidth?: number; 
    dashed?: boolean; 
    dashSize?: number;
    gapSize?: number; 
}

export function SimpleLine({
    points,
    color = 'black',
    lineWidth = 1,
    dashed = false,
    dashSize = 0.2,
    gapSize = 0.15,
}: SimpleLineProps) {
    const positions = useMemo(() => new Float32Array(points.flat()), [points]);
    const geometry = useMemo(() => {
        const lineGeometry = new LineGeometry();
        lineGeometry.setPositions(positions);
        return lineGeometry;
    }, [positions]);
    const material = useMemo(() => new LineMaterial(), []);
    const line = useMemo(() => new Line2(geometry, material), [geometry, material]);

    useEffect(() => {
        material.color.set(color);
        material.linewidth = lineWidth;
        material.dashed = dashed;
        material.dashSize = dashSize;
        material.gapSize = gapSize;
        material.needsUpdate = true;
    }, [material, color, lineWidth, dashed, dashSize, gapSize]);

    useEffect(() => () => geometry.dispose(), [geometry]);
    useEffect(() => () => material.dispose(), [material]);

    useFrame(() => {
        material.resolution.set(window.innerWidth, window.innerHeight);
    });

    return <primitive object={line} />;
    
}
